import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AddProductModal } from './components/AddProductModal';
import { PairsWithItem } from './components/PairsWithItem';
import { RemoveConfirmModal } from './components/RemoveConfirmModal';

import { Carousel, CarouselSlide } from '@/components/Carousel';
import { useGetProducts } from '@/hooks/useGetProducts';
import { useUpdatePairsWith } from '@/hooks/useUpdatePairsWith';
import { cn, getAxiosErrorMessage } from '@/lib/utils';
import { Product, UpdatePairsWithDto } from '@/services/ProductsService';

interface PairsWithProps {
  product?: Product;
}

export function PairsWith({ product }: PairsWithProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.VisionProduct.components.PairsWith',
  });

  const [isAddProductModalOpen, setAddProductModalOpen] = useState(false);
  const [currentlyLoadingPairsWithId, setCurrentlyLoadingPairsWithId] =
    useState<null | string>(null);
  const [pairsWithIdToRemove, setPairsWithIdToRemove] = useState<null | string>(
    null,
  );

  const pairsWith = product?.shopTheLook?.split(',') ?? [];

  const { products } = useGetProducts({ productId: pairsWith[0] });

  const { updatePairsWith, isLoading } = useUpdatePairsWith(product!.id);

  function handleOpenAddProductModal() {
    setAddProductModalOpen(true);
  }

  function handleCloseAddProductModal() {
    setAddProductModalOpen(false);
  }

  function handleSetPairsWithIdToRemove(shopTheLookId: null | string) {
    setPairsWithIdToRemove(shopTheLookId);
  }

  async function handleuUdatePairsWith(updatePairsWithDto: UpdatePairsWithDto) {
    setCurrentlyLoadingPairsWithId(updatePairsWithDto.pairsWithId);

    try {
      await updatePairsWith(updatePairsWithDto);
    } catch (error) {
      const errorMessage = getAxiosErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setCurrentlyLoadingPairsWithId(null);
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xl font-semibold">{t('title')}</h3>

      <div>
        <Carousel
          navigation={{ enabled: !!(pairsWith && pairsWith.length >= 2) }}
          allowTouchMove={false}
          slidesPerView={2}
          spaceBetween={4}
        >
          {pairsWith?.map((pw) => {
            if (!pw) {
              return null;
            }

            return (
              <CarouselSlide key={pw}>
                <PairsWithItem
                  pairsWith={pw}
                  onRemove={handleSetPairsWithIdToRemove}
                />
              </CarouselSlide>
            );
          })}

          <CarouselSlide>
            <div
              className="relative"
              onClick={() => handleOpenAddProductModal()}
            >
              {products && products.length > 0 && (
                <div className="opacity-0">
                  <div>
                    <img
                      className="rounded-t-lg"
                      src={products[0].image1}
                      alt={products[0].name}
                    />
                  </div>

                  <div className="p-5 font-light">
                    <span className="block truncate text-xs">
                      {products[0].name}
                    </span>
                  </div>
                </div>
              )}

              <div
                className={cn(
                  'group top-0 flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-l border-2 border-dashed border-gray-300 p-3 transition-shadow hover:shadow-md',
                  products && products.length > 0 && 'absolute',
                )}
              >
                <div className="mb-3 flex aspect-square w-full items-center justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 group-hover:bg-gray-200">
                    <span className="text-2xl text-gray-600 group-hover:text-gray-500">
                      +
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CarouselSlide>
        </Carousel>
      </div>

      <AddProductModal
        open={isAddProductModalOpen}
        productId={product!.productId}
        pairsWith={pairsWith}
        currentlyLoadingPairsWithId={currentlyLoadingPairsWithId}
        onUpdatePairsWith={handleuUdatePairsWith}
        onClose={handleCloseAddProductModal}
      />

      <RemoveConfirmModal
        open={!!pairsWithIdToRemove}
        productId={product!.productId}
        pairsWithId={pairsWithIdToRemove}
        isLoading={isLoading}
        onUpdatePairsWith={handleuUdatePairsWith}
        onClose={handleSetPairsWithIdToRemove}
      />
    </div>
  );
}
